/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the base layout for the browse view.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { useState, useCallback } from 'react';

/**
 * Utility used to generate a Hook to allow for the creation of styles using JSS.
 */
import { makeStyles } from '@material-ui/core/styles';

/**
 * Material UI Paper component.
 */
import Paper from '@material-ui/core/Paper';

/**
 * Material UI Typography component.
 */
import { Typography } from '@material-ui/core';

/**
 * Material UI TextField component.
 */
import TextField from '@material-ui/core/TextField';

/**
 * Material UI Breadcrumbs component.
 */
import Breadcrumbs from '@material-ui/core/Breadcrumbs';

/**
 * Material UI Button component.
 */
import Button from '@material-ui/core/Button';

/**
 * Material UI Link component.
 */
import Link from '@material-ui/core/Link';

/**
 * Material UI Grid component.
 */
import Grid from '@material-ui/core/Grid';

/**
 * Typings
 */
import { RouteChildrenProps } from 'react-router';

/**
 * Used to navigate around the app.
 */
import { Link as RouterLink } from 'react-router-dom';

/**
 * Allows actions to be dispatched to the redux store.
 */
import { useDispatch } from 'react-redux';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/**
 * Enables the use of term data loaded from the ServiceStack API.
 */
import useTerm from 'src/hooks/useTerm';

/**
 * Enables the use of global app state.
 */
import { useAppSelector, AppSelectors, AppActions } from 'src/store/reducers/app';

/**
 * Enumeration
 */
import { RequestState } from 'src/store/reducers';

/**
 * Loading indicator
 */
import Loading from 'src/components/Loading';

/**
 * Converts \n's to <br />'s
 */
import convertReturnsToHtml from 'src/utils/convertReturnsToHtml';

/**
 * Used to limit results to the current version only.
 */
import useVersionCheck from 'src/hooks/useVersionCheck';

/**
 * used to update app state and post messages to opener window.
 */
import useAppState from 'src/hooks/useAppState';

/**
 * Enumeration
 */
import { WindowView } from 'src/utils/MessageManager';

/**
 * Detects whether the app was opened by a parent window.
 */
import hasParentWindow from 'src/hooks/hasParentWindow';

/**
 * allows for selection of a grade.
 */
import useAppSelect from 'src/hooks/useAppSelect';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces / Types
 * ---------------------------------------------------------------------------------
 */

interface IBrowseTermParams {
    termId: string;
}

interface IBrowseTermProps extends RouteChildrenProps<IBrowseTermParams> {

}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles(theme => ({
    title: {
        margin: theme.spacing(3, 0)
    },
    paper: {
        // padding: theme.spacing(3, 2),
        margin: theme.spacing(3, 0)
    },
    list: {
        width: '100%',
        padding: theme.spacing(0,0)
    },
    breadcrumbs: {
        margin: theme.spacing(3, 0)
    },
    textField: {
        width: '100%'
    },
    fieldName: {
        margin: theme.spacing(1, 0, 0, 0),
        [theme.breakpoints.up('md')]: {
            margin: 0
        }
    },
    fieldDescription: {
        margin: theme.spacing(1, 0, 0, 0),
        fontStyle: 'italic'
    },
    fieldSpecification: {
        margin: theme.spacing(1, 0, 0, 0)
    },
    bottomButtonContainer: {
        textAlign: 'right',
        margin: theme.spacing(3, 0)
    },
    topButtonContainer: {
        textAlign: 'right'
    }
}));

const useGridStyles = makeStyles(theme => ({
    gridRow: {
        width: 'auto',
        padding: theme.spacing(1, 2),
        borderBottom: `1px solid rgba(0, 0, 0, 0.12)`
    },
    gridTitle: {

    },
    gridValue: {
        alignSelf: 'center'
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

// const limit = 5;

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

const BrowseTermGridRow: React.FunctionComponent<{label: string; value: React.ReactNode; }> = ({
    label,
    value
}) => {
    const classes = useGridStyles();

    return (
        <Grid
            container
            className={classes.gridRow}
        >
            <Grid
                item
                className={classes.gridTitle}
                xs={12}
                sm={12}
                md={3}
                lg={2}
                xl={2}
            >
                <Typography
                    variant="h6"
                >
                    {label}
                </Typography>
            </Grid>
            <Grid
                item
                xs={12}
                sm={12}
                md={9}
                lg={10}
                xl={10}
                className={classes.gridValue}
            >
                {value}
            </Grid>
        </Grid>
    )
}

const BrowseTerm: React.FunctionComponent<IBrowseTermProps> = ({
    match
}) => {
    const classes = useStyles();

    if (!match || !match.params.termId) {
        return null;
    }

    const allowSelection = hasParentWindow();

    const [version, category, parentTerm, term, loadState] = useTerm(parseInt(match.params.termId, 10));

    useVersionCheck(version);

    useAppState(
        WindowView.Browse,
        version,
        category,
        parentTerm ? parentTerm : term,
        parentTerm ? term : null,
        null
    );

    const specificationdRequired = (parentTerm && parentTerm.specifyOther) || (term && term.specifyOther) ? true : false;
    const specificationTermId = specificationdRequired ?
        term && term.specifyOther ?
            term.id :
            parentTerm ?
                parentTerm.id :
                null :
        null;

    const specification = useAppSelector(state => {
        const spec = AppSelectors.selectSpecification(state, specificationTermId);

        return spec ? spec : '';
    });

    const dispatch = useDispatch();

    const [ touched, setTouched ] = useState<boolean>(false);

    const onSpecChange = useCallback((event: React.SyntheticEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement, Event>) => {
        if (event.target) {
            const element: HTMLInputElement = event.target as HTMLInputElement;

            dispatch(AppActions.setSpecification(specificationTermId, element.value));
            setTouched(true);
        }
    }, [dispatch, specificationTermId, setTouched]);

    const onSpecBlur = useCallback(() => {
        setTouched(true);
    }, [setTouched]);

    const invalid = specificationdRequired && !specification.trim();
    const error = invalid && touched;

    const select = useAppSelect(
        WindowView.Browse,
        version,
        category,
        parentTerm ? parentTerm : term,
        parentTerm ? term : null,
        null
    );

    const onSelection = useCallback(() => {
        if (invalid) {
            setTouched(true);
        }
        else {
            select(specification);
        }
    }, [select, specification, setTouched, specificationdRequired])


    const targetTermId = useAppSelector(AppSelectors.selectTargetTermId);

    const loading = loadState && loadState.state === RequestState.Pending;

    const specificationElement = allowSelection ?
        (
            <div
                className={classes.fieldSpecification}
            >
                <TextField
                    error={error}
                    id="standard-error"
                    label="Other, specify"
                    value={specification}
                    onChange={onSpecChange}
                    onBlur={onSpecBlur}
                    className={classes.textField}
                    margin="none"
                    helperText={ error ? 'Specification must be entered to select this grade.' : undefined }
                />
            </div>
        ) :
        (
            <div
                className={classes.fieldSpecification}
            >
                <strong>Specification Required</strong>
            </div>
        )

    return (
        <>
            {
                version && category && term ?
                    <Breadcrumbs
                        className={classes.breadcrumbs}
                        separator="›"
                        aria-label="Breadcrumb"
                    >
                        {
                            targetTermId ?
                                <Typography>
                                    Categories
                                </Typography>:
                                <Link
                                    component={RouterLink}
                                    to={`/${version.code}/browse`}
                                >
                                    Categories
                                </Link>
                        }
                        {
                            targetTermId ?
                                <Typography>
                                    {category.name}
                                </Typography>:
                                <Link
                                    component={RouterLink}
                                    to={`/${version.code}/browse/category/${category.id}`}
                                >
                                    {category.name}
                                </Link>
                        }
                        {
                            parentTerm ?
                                targetTermId && targetTermId !== parentTerm.id ?
                                    <Typography>
                                        {parentTerm.name}
                                    </Typography>:
                                    <Link
                                        component={RouterLink}
                                        to={`/${version.code}/browse/term/${parentTerm.id}`}
                                    >
                                        {parentTerm.name}
                                    </Link>:
                                null
                        }
                        {
                            targetTermId && targetTermId !== term.id && (!parentTerm || targetTermId !== parentTerm.id) ?
                                <Typography>
                                    {term.name}
                                </Typography>:
                                <Link
                                    component={RouterLink}
                                    to={`/${version.code}/browse/term/${term.id}`}
                                >
                                    {term.name}
                                </Link>
                        }
                        <Typography color="textPrimary">
                            Unknown Grade
                        </Typography>
                    </Breadcrumbs> :
                    <Breadcrumbs
                        className={classes.breadcrumbs}
                        separator="›"
                        aria-label="Breadcrumb"
                    >
                        <Typography>
                            Categories
                        </Typography>
                    </Breadcrumbs>
            }
            {
                allowSelection && term && (
                    <div
                        className={classes.topButtonContainer}
                    >
                        <Button
                            color="primary"
                            variant="contained"
                            onClick={onSelection}
                        >
                            Select Grade
                        </Button>
                    </div>
                )
            }
            <Paper
                className={classes.paper}
            >
                {
                    loading && (
                        <Loading />
                    )
                }
                <BrowseTermGridRow
                    label="CTCAE Version"
                    value={(
                        <Typography
                            className={classes.fieldName}
                        >
                            {version ? version.name : ''}
                        </Typography>
                    )}
                />
                <BrowseTermGridRow
                    label="Category"
                    value={(
                        <Typography
                            className={classes.fieldName}
                        >
                            {category ? category.name : ''}
                        </Typography>
                    )}
                />
                {
                    parentTerm && parentTerm.medDraCode && (!term || !term.medDraCode) && (
                        <BrowseTermGridRow
                            label="MedDRA Code"
                            value={(
                                <Typography
                                    className={classes.fieldName}
                                >
                                    {parentTerm.medDraCode}
                                </Typography>
                            )}
                        />
                    )
                }
                {
                    term && term.medDraCode && (
                        <BrowseTermGridRow
                            label="MedDRA Code"
                            value={(
                                <Typography
                                    className={classes.fieldName}
                                >
                                    {term.medDraCode}
                                </Typography>
                            )}
                        />
                    )
                }
                {
                    parentTerm && (
                        <BrowseTermGridRow
                            label="Term"
                            value={(
                                <>
                                    <Typography
                                        className={classes.fieldName}
                                    >
                                        {parentTerm.name}
                                    </Typography>
                                    {
                                        parentTerm.description && (
                                            <Typography
                                                className={classes.fieldDescription}
                                            >
                                                {convertReturnsToHtml(parentTerm.description)}
                                            </Typography>
                                        )
                                    }
                                    {
                                        (!term || !term.specifyOther) && parentTerm.specifyOther && (
                                            specificationElement
                                        )
                                    }
                                </>
                            )}
                        />
                    )
                }
                {
                    parentTerm && parentTerm.note && (
                        <BrowseTermGridRow
                            label="Note"
                            value={(
                                <Typography
                                    className={classes.fieldName}
                                >
                                    {parentTerm.note}
                                </Typography>
                            )}
                        />
                    )
                }
                {
                    term && (
                        <BrowseTermGridRow
                            label="Sub-Term"
                            value={(
                                <>
                                    <Typography
                                        className={classes.fieldName}
                                    >
                                        {term.name}
                                    </Typography>
                                    {
                                        term.description && (
                                            <Typography
                                                className={classes.fieldDescription}
                                            >
                                                {convertReturnsToHtml(term.description)}
                                            </Typography>
                                        )
                                    }
                                    {
                                        term.specifyOther && (
                                            specificationElement
                                        )
                                    }
                                </>
                            )}
                        />
                    )
                }
                {
                    term && term.note && (
                        <BrowseTermGridRow
                            label="Note"
                            value={(
                                <Typography
                                    className={classes.fieldName}
                                >
                                    {term.note}
                                </Typography>
                            )}
                        />
                    )
                }
                <BrowseTermGridRow
                    label="Grade"
                    value={(
                        <>
                            <Typography
                                className={classes.fieldName}
                            >
                                Unknown Grade
                            </Typography>
                            <Typography
                                className={classes.fieldDescription}
                            >
                                The exact grade of the event is not known
                            </Typography>
                        </>
                    )}
                />
            </Paper>
            {
                allowSelection && term && (
                    <div
                        className={classes.bottomButtonContainer}
                    >
                        <Button
                            color="primary"
                            variant="contained"
                            onClick={onSelection}
                        >
                            Select Grade
                        </Button>
                    </div>
                )
            }
        </>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Exports - Default
 * ---------------------------------------------------------------------------------
 */

export default BrowseTerm;


// WEBPACK FOOTER //
// ./src/containers/BrowseTerm.tsx